import type {
    ApiPriceData,
    ApiProduct,
    ApiProductVariant,
} from '../../../../models/ecommerce/product/ApiProduct';
import type {
    ApiAppliedVouchers,
    ApiCart,
    ApiCartData,
    ApiCartItemRecipeData,
    ApiCartRecipe,
    ApiDeliveryMode,
    ApiFullCartItem,
    ApiPromotionResult,
    ApiSubstituteProductResponse,
    ApiTaxShare,
    EcommerceEcoupon,
} from '../../api/cart/responses/cartResponses';
import type { ApiCartTimeslot } from '../../api/timeslots/timeslotModels';
import type {
    EcomPromotion,
    EdlpEcomPromotion,
    FixedEcomPromotion,
    MultiBuyEcomPromotion,
    PercentageEcomPromotion,
} from '../../models/domain/ecommerce/promotion/promotion';
import type { HybrisCart, HybrisOrderEntry } from '../../models/hybris/hybrisCart';
import type { HybrisDeliveryMode } from '../../models/hybris/hybrisDeliverMode';
import type { HybrisOrderRecipeEntry } from '../../models/hybris/hybrisOrderRecipeEntry';
import type { HybrisPrice } from '../../models/hybris/hybrisPrice';
import type { VarianceData } from '../../models/hybris/hybrisProduct';
import type { HybrisTaxValue } from '../../models/hybris/hybrisTaxValue';
import type { HybrisTimeWindow } from '../../models/hybris/hybrisTimeWindow';
import type { HybrisVoucher } from '../../models/hybris/hybrisVoucher';

import { appInsights } from '../../../../appInsights';
import { mapTimeslot } from '../../api/timeslots/mapTimeslot';
import { mapPromotionMaxUsage } from '../../models/domain/ecommerce/promotion/promotionMappers';
import { DeliveryMode } from '../../store/structureDefinitions/storeState';
import { isEdlpPricesEnabled } from '../../utils/edlpFeatureFlag';

const getVariantName = (varianceCode: string, product: ApiProduct) => {
    if (product.variants && product.variants.length > 0) {
        const variantProduct = product.variants.find((variant) => variant.id === varianceCode);
        return variantProduct && variantProduct.name;
    }
    return undefined;
};

export const mapProductToApiFullCartItem = (
    result: ApiSubstituteProductResponse,
    product: ApiProduct,
): ApiFullCartItem => {
    const cartItem: ApiFullCartItem = {
        id: result.entryNumber.toString(),
        name: result.name,
        productId: result.productIdentifier,
        variantId: result.variantId,
        quantity: result.quantity,
        manufacturer: result.manufacturer,
    };

    if (product) {
        cartItem.image = product.image && product.image.url;
        cartItem.priceData = product.priceData;
        cartItem.productUrl = product.url;
        cartItem.variantName = getVariantName(result.variantId, product);
    }

    if (product && product.details) {
        cartItem.packageSizeInformation = `${product.details.size && product.details.size.size} ${
            product.details.size.unit && product.details.size.unit.toLowerCase()
        }`;
    }

    return cartItem;
};

const mapPrice = (hybrisPrice?: HybrisPrice) => {
    return {
        inclTaxPrice: hybrisPrice?.value,
        exclTaxPrice: hybrisPrice?.valueWithoutTax,
    } as ApiPriceData;
};

const mapCartItemPrice = (hybrisPrice?: HybrisPrice) => {
    if (!hybrisPrice) {
        return undefined;
    }

    return mapPrice(hybrisPrice);
};

const mapVariant = (variant: VarianceData): ApiProductVariant => {
    return {
        id: variant.code,
        name: variant.name,
        priceData: mapPrice(variant.price),
    };
};

const mapCartItem = (item: HybrisOrderEntry) => {
    const cartItem: ApiFullCartItem = {
        id: item.entryNumber.toString(),
        variantId: item.variance?.code,
        variantName: item.variance?.name,
        name: item.product.name,
        discount: mapCartItemPrice(item.discount),
        totalPriceData: mapCartItemPrice(item.totalPrice),
        totalPriceWithoutDiscount: mapCartItemPrice(item.totalPriceWithoutDiscount),
        quantity: item.quantity,
        recycleFeePiecePriceData: mapCartItemPrice(item.depositData?.deposit),
        recycleFeeTotalPriceData: mapCartItemPrice(item.depositData?.totalDeposit),
        itemReplaceable: item.replace,
        partOfRecipe: item.partOfRecipe,

        // fields from product
        productId: item.product.code,
        piecePrice: mapCartItemPrice(item.pickBasePrice),
        priceData: mapCartItemPrice(item.product?.pickPrice),
        variants: item.product.variances?.map((variant) => mapVariant(variant)),
        manufacturer: item.product.manufacturer,
        packageSizeInformation: item.product.packageSizeInformation,
        image: item.product.images?.find((image) => !!image.url)?.url,
        productUrl: `/redirect/ecommerce/product/${encodeURIComponent(item.product.code)}`,
    };

    return cartItem;
};

const calculateLeftToCheckout = (hybrisCart: HybrisCart) => {
    if (hybrisCart.minimumShoppingValueReached) return 0;

    if (!hybrisCart.amountMissing) {
        return 0;
    }

    return hybrisCart.amountMissing.value > 0 ? hybrisCart.amountMissing.value : 0;
};

const isPickupDelivery = (deliveryMode: HybrisDeliveryMode) => {
    return (
        deliveryMode.code === 'pickup' ||
        deliveryMode.code === 'pickup_drive_through' ||
        deliveryMode.code === 'pickup_from_lockers' ||
        deliveryMode.code === 'pickup_from_staff' ||
        deliveryMode.code.includes('pickup') // to catch others if they exist
    );
};

const resolveDeliveryMode = (hybrisDeliveryMode?: HybrisDeliveryMode): ApiDeliveryMode => {
    if (!!hybrisDeliveryMode && hybrisDeliveryMode.code === 'homedelivery') {
        return {
            mode: DeliveryMode.homedelivery,
            code: hybrisDeliveryMode.code,
            name: hybrisDeliveryMode.name,
        };
    }

    if (!!hybrisDeliveryMode && isPickupDelivery(hybrisDeliveryMode)) {
        return {
            mode: DeliveryMode.pickup,
            code: hybrisDeliveryMode.code,
            name: hybrisDeliveryMode.name,
        };
    }

    return { mode: DeliveryMode.undefined, code: 'undefined', name: '' };
};

const calculateExpirationDiff = (expirationTime: Date) => {
    const milisecondsDiff = expirationTime.getTime() - Date.now();
    return milisecondsDiff;
};

const resolveTimeslot = (
    hybrisTimeSlot: HybrisTimeWindow,
    cartTimeSlotExpirationTime?: string,
): ApiCartTimeslot | null => {
    if (!cartTimeSlotExpirationTime) {
        return null;
    }

    const expirationTime = new Date(cartTimeSlotExpirationTime);
    const milisecondsDiff = calculateExpirationDiff(expirationTime);

    const isExpired = milisecondsDiff <= 0;

    // Hybris is sending us Timeslot even though the expiration time has passed.
    // They are not clearing it immediately, they wait for a scheduled job (I think it is every 5 minutes) to clear timeslot from cart.
    // That means, if we were mapping normally like they do, user would have timeslot valid for 15 minuts + around 5 minutes in worst case scenario. That is currently behaviour on Hybris storefront, and its wrong.
    if (!isExpired && hybrisTimeSlot.id) {
        const timeslot = mapTimeslot(hybrisTimeSlot)! as ApiCartTimeslot;
        timeslot.expirationTime = expirationTime.valueOf();
        timeslot.milisecondsToExpire = milisecondsDiff;
        return timeslot;
    }

    // since hybris doesnt remove expired timeslot instantly, we fake it here. If timeslot is expired, imitiate as if there is not timeslot.
    return null;
};

const resolveVouchers = (hybrisCart: HybrisCart): ApiAppliedVouchers[] => {
    const findVoucherInAppliedPromotions = (hybrisVoucher: HybrisVoucher) => {
        return hybrisCart.appliedOrderPromotions?.find(
            (promo) => promo.fromVoucher && promo.voucherCodes?.includes(hybrisVoucher.voucherCode),
        );
    };

    const vouchers = hybrisCart.appliedVouchers.map((voucher) => {
        const promotion = findVoucherInAppliedPromotions(voucher);
        return {
            voucherCode: voucher.voucherCode,
            compensationVoucher: voucher.compensationVoucher,
            externalReason: voucher.externalReason,
            freeShipping: voucher.freeShipping,
            notAppliedHint: voucher.notAppliedHint,
            triggeredPromotion: voucher.triggeredPromotion,
            name: voucher.name,
            description: promotion?.description,
            priceData: {
                inclTaxPrice: voucher.value,
                exclTaxPrice: promotion?.totalDiscountWithoutTax,
            },
        };
    });

    return vouchers;
};

const mapEcoupons = (hybrisEcoupons: HybrisCart['ecoupons']): EcommerceEcoupon[] => {
    if (!hybrisEcoupons) {
        return [];
    }

    return hybrisEcoupons.map((ecoupon) => ({
        couponId: ecoupon.couponId,
        title: ecoupon.title,
        active: ecoupon.active,
        expectedValue: ecoupon.expectedValue,
        expirationTime: ecoupon.expirationTime,
        triggeredPromotion: ecoupon.triggeredPromotion,
        notAppliedHint: ecoupon.notAppliedHint,
        freeShipping: ecoupon.freeShipping,
        offerType: ecoupon.offerType,
        isDirty: false,
    }));
};

const resolveOrderPromptions = (hybrisCart: HybrisCart): ApiPromotionResult[] => {
    if (!hybrisCart.appliedOrderPromotions?.length) return [];
    const findVoucherInAppliedPromotions = (voucherCodes: string[] | undefined) => {
        if (!voucherCodes?.length) return undefined;
        return hybrisCart.appliedVouchers.find((voucher) =>
            voucherCodes.includes(voucher.voucherCode),
        );
    };
    return hybrisCart.appliedOrderPromotions.map((promo) => {
        const voucher = findVoucherInAppliedPromotions(promo.voucherCodes);
        return {
            description:
                (voucher?.compensationVoucher && 'Värdekupong') ||
                promo.description ||
                voucher?.name ||
                (voucher?.freeShipping && 'Fri frakt') ||
                '',
            priceData: {
                inclTaxPrice: promo.totalDiscount,
                exclTaxPrice: promo.totalDiscountWithoutTax,
            },
            promotion: {
                description: promo.promotion.description,
                code: promo.promotion.code,
            },
            fromManuallyAddedVoucher: !!voucher && !voucher.compensationVoucher,
        };
    });
};

const mapTaxShares = (hybrisTaxValues?: HybrisTaxValue[]): ApiTaxShare[] => {
    const taxValues = hybrisTaxValues || [];

    return taxValues.map((hTaxValue) => ({
        appliedValue: hTaxValue.appliedValue?.value || 0,
        rate: Math.trunc(hTaxValue.value),
    }));
};

const mapCartData = (hybrisCart: HybrisCart): ApiCartData => {
    const cartData: ApiCartData = {
        cartId: hybrisCart.guid,
        name: hybrisCart.name || '',
        quantity: hybrisCart.totalQuantity,
        leftToCheckout: calculateLeftToCheckout(hybrisCart),
        minimumShoppingValue: mapPrice(hybrisCart.minimumShoppingValue),
        extraAmountToBeReserved: hybrisCart.extraAmountToBeReserved?.value || 0,
        productSumWithoutDiscountPriceData: mapPrice(hybrisCart.subtotalWithoutDiscount),
        totalProductPriceWithDiscounts: mapPrice(hybrisCart.totalProductPriceWithDiscounts),
        productSumPriceData: mapPrice(hybrisCart.subTotal),
        totalDepositSum: mapPrice(hybrisCart.totalDepositSum),
        totalPriceData: mapPrice(hybrisCart.totalPriceWithExtraAmountToReserve),
        deliveryFeePriceData: mapPrice(hybrisCart.deliveryCost),
        totalPriceExclReservationFee: mapPrice(hybrisCart.totalPrice),
        tax: hybrisCart.totalTax ? hybrisCart.totalTax.value : 0,
        totalTaxValues: mapTaxShares(hybrisCart.totalTaxValues),
        deliveryMode: resolveDeliveryMode(hybrisCart.deliveryMode),
        totalDiscountPriceData: mapPrice(hybrisCart.totalDiscounts),
        productDiscountPriceData: mapPrice(hybrisCart.productDiscounts),
        timeslot: resolveTimeslot(hybrisCart.timeWindow, hybrisCart.timeWindowExpiryTime),
        paymentExpirationDate: hybrisCart.klarnaExpirationTime
            ? new Date(hybrisCart.klarnaExpirationTime)
            : undefined,
        postCode: hybrisCart.postCode,
        appliedVouchers: resolveVouchers(hybrisCart),
        appliedOrderPromotions: resolveOrderPromptions(hybrisCart),
        editOrderNumber: hybrisCart.editedOrderCode || null,
        editOrderTimeout: hybrisCart.editTimeout || null,
        isInEditOrderMode: !!hybrisCart.editedOrderCode,
        editOrderSummary: {
            editOrderTotalDifferencePriceData: mapPrice(hybrisCart.editTotalPriceDifference),
            editOrderTotalPriceData: mapPrice(hybrisCart.editedOrderTotalPrice),
            editOrderSubtotalDifferencePriceData: mapPrice(hybrisCart.editSubtotalDifference),
            editOrderSubtotalPriceData: mapPrice(hybrisCart.editedOrderSubtotal),
        },
        store: hybrisCart.coopStore
            ? { id: hybrisCart.coopStore.code, name: hybrisCart.coopStore.name }
            : undefined,
        ...(hybrisCart.pointOfService && {
            pickupStore: {
                id: hybrisCart.pointOfService.name,
                productionUnitId: hybrisCart.pointOfService.storeId,
                name: hybrisCart.pointOfService.displayName,
                pickupDeliveryModes: hybrisCart.pointOfService.pickupDeliveryModes,
            },
        }),
        commentAboutCustomer: hybrisCart.commentAboutCustomer,
    };

    return cartData;
};

const isGoodToHaveRecipeGroup = (groupCode: string) => {
    const GOOD_TO_HAVE_RECIPE_CODE = 'goodToHave';
    return groupCode.toLowerCase() === GOOD_TO_HAVE_RECIPE_CODE.toLowerCase();
};

const isSharedRecipeGroup = (groupCode: string) => {
    const SHARED_RECIPE_CODE = 'shared';
    return groupCode.toLowerCase() === SHARED_RECIPE_CODE.toLowerCase();
};

const mapCartItems = (hybrisCart: HybrisCart, entries: HybrisOrderEntry[]) => {
    const cartItems = entries.map((entry) => mapCartItem(entry));
    const recipeDataItems: { productId: string; recipeData: ApiCartItemRecipeData }[] = [];

    hybrisCart.recipesGroups.forEach((rg) => {
        // good to have group has different objects than the others :(
        if (isGoodToHaveRecipeGroup(rg.code)) {
            if (rg.recommendedProducts) {
                rg.recommendedProducts.recipeProductsData.forEach((rpd) =>
                    recipeDataItems.push({
                        productId: rpd.code,
                        recipeData: {
                            code: rg.code,
                            name: rg.name,
                            portions: 0,
                            recipeQuantity: rpd.quantity,
                            substituteIdentifier: rpd.substituteIdentifier,
                            substituteProductsAmount: rpd.substituteProductsAmount,
                            includedInRecipes: hybrisCart.recipesGroups
                                .filter(
                                    (includedRg) =>
                                        !isGoodToHaveRecipeGroup(includedRg.code) &&
                                        !!includedRg.recommendedProducts?.recipeProductsData.some(
                                            (x) => x.code === rpd.code,
                                        ),
                                )
                                .map((x) => ({ code: x.code, name: x.name })),
                        },
                    }),
                );
            }
        } else {
            rg.recipeEntries.forEach((re) => {
                recipeDataItems.push({
                    productId: re.orderEntry.product.code,
                    recipeData: {
                        code: rg.code,
                        name: rg.name,
                        portions: rg.portions || 0,
                        recipeQuantity: re.recipeQuantity,
                        substituteIdentifier: re.substituteId,
                        substituteProductsAmount: re.substituteProductsAmount,
                        includedInRecipes:
                            re.recipes?.map((x) => ({ code: x.code, name: x.name })) || [],
                    },
                });
            });
        }
    });

    for (let i = 0; i < cartItems.length; i += 1) {
        if (cartItems[i].partOfRecipe) {
            const joinedRecipeItem = recipeDataItems.find(
                (rci) => rci.productId === cartItems[i].productId,
            );
            if (joinedRecipeItem) {
                cartItems[i].recipeData = {
                    code: joinedRecipeItem.recipeData.code,
                    name: joinedRecipeItem.recipeData.name,
                    portions: joinedRecipeItem.recipeData.portions,
                    recipeQuantity: joinedRecipeItem.recipeData.recipeQuantity,
                    substituteIdentifier: joinedRecipeItem.recipeData.substituteIdentifier,
                    substituteProductsAmount: joinedRecipeItem.recipeData.substituteProductsAmount,
                    includedInRecipes: joinedRecipeItem.recipeData.includedInRecipes,
                };
            }
        }
    }

    return cartItems;
};

// it may be possible to remove this later as it seems that promos are direclty on cart items. But we dont know for sure that its always like that on all stores. Move promos on items and remove this when confirmed
const mapProductPromos = (hybrisCart: HybrisCart) => {
    const promos: EcomPromotion[] = [];

    hybrisCart.appliedProductPromotions
        .filter((hPromo) => hPromo.consumedEntries.length > 0)
        .forEach((hPromo) => {
            const entries = hybrisCart.entries.filter((entry) =>
                hPromo.consumedEntries.some((ce) => ce.orderEntryNumber === entry.entryNumber),
            );

            entries.forEach((entry) => {
                switch (hPromo.promotion.promotionType) {
                    case 'PERCENTAGE': {
                        const promo: PercentageEcomPromotion = {
                            identifier: hPromo.promotion.code,
                            description: hPromo.promotion.description,
                            isMemberPrice: hPromo.promotion.medmera,
                            maxUseText: hPromo.promotion.maxUseText,
                            maxUse: mapPromotionMaxUsage(hPromo.promotion),
                            type: hPromo.promotion.promotionType,
                            productId: entry.product.code,
                            variantId: entry.variance?.code,
                            percentageDiscount: hPromo.promotion.percentageDiscount,
                            higherThanHistoricalPrice: false, // hybris is missing this information
                        };
                        promos.push(promo);
                        break;
                    }
                    case 'MULTIBUY': {
                        const promo: MultiBuyEcomPromotion = {
                            identifier: hPromo.promotion.code,
                            description: hPromo.promotion.description,
                            isMemberPrice: hPromo.promotion.medmera,
                            maxUseText: hPromo.promotion.maxUseText,
                            maxUse: mapPromotionMaxUsage(hPromo.promotion),
                            type: hPromo.promotion.promotionType,
                            productId: entry.product.code,
                            variantId: entry.variance?.code,
                            numberOfProductRequired: hPromo.promotion.numberOfProductRequired,
                            bundlePrice: {
                                inclTaxPrice: hPromo.promotion.bundlePrice,
                                exclTaxPrice: undefined, // should be taken from hybris if it was there
                            },
                        };
                        promos.push(promo);
                        break;
                    }
                    case 'FIXED_PRICE': {
                        const promo: FixedEcomPromotion = {
                            identifier: hPromo.promotion.code,
                            description: hPromo.promotion.description,
                            isMemberPrice: hPromo.promotion.medmera,
                            maxUseText: hPromo.promotion.maxUseText,
                            maxUse: mapPromotionMaxUsage(hPromo.promotion),
                            type: hPromo.promotion.promotionType,
                            productId: entry.product.code,
                            variantId: entry.variance?.code,
                            fixedPrice: {
                                inclTaxPrice: hPromo.promotion.fixedUnitPrice,
                                exclTaxPrice: undefined, // should be taken from hybris if it was there
                            },
                            fixedPiecePrice: {
                                inclTaxPrice: undefined,
                                exclTaxPrice: undefined, // should be taken from hybris if it was there
                            },
                            higherThanHistoricalPrice: false, // hybris is missing this information
                        };
                        promos.push(promo);
                        break;
                    }
                    case 'EDLP': {
                        if (!isEdlpPricesEnabled() || !hPromo.promotion.numberOfProductRequired) {
                            break;
                        }
                        const promo: EdlpEcomPromotion = {
                            identifier: hPromo.promotion.code,
                            description: hPromo.promotion.description,
                            isMemberPrice: hPromo.promotion.medmera,
                            maxUseText: hPromo.promotion.maxUseText,
                            maxUse: mapPromotionMaxUsage(hPromo.promotion),
                            type: 'EDLP',
                            productId: entry.product.code,
                            variantId: entry.variance?.code,
                            numberOfProductRequired: hPromo.promotion.numberOfProductRequired,
                            bundlePrice: {
                                inclTaxPrice: hPromo.promotion.bundlePrice,
                                exclTaxPrice: undefined,
                            },
                        };
                        promos.push(promo);
                        break;
                    }
                    default:
                        break;
                }
            });
        });

    return promos;
};

const getRecipeType = (hybrisRecipe: HybrisOrderRecipeEntry): ApiCartRecipe['type'] => {
    if (isSharedRecipeGroup(hybrisRecipe.code)) return 'shared';
    if (isGoodToHaveRecipeGroup(hybrisRecipe.code)) return 'common';

    return 'recipe';
};

const mapRecipes = (hybrisCart: HybrisCart) => {
    const recipes: ApiCartRecipe[] = [];

    const allSharedRecipeEntries =
        hybrisCart.recipesGroups.find((asre) => isSharedRecipeGroup(asre.code))?.recipeEntries ||
        [];

    hybrisCart.recipesGroups.forEach((rg) => {
        const recipeSection: ApiCartRecipe = {
            code: rg.code,
            type: getRecipeType(rg),
            calculationType: rg.calculation,
            prioritizationType: rg.prioritization,
            name: rg.name,
            portions: rg.portions,
            imageUrl: rg.imageUrl,
            group: rg.group,
            productsMap: [],
            sharedProductsMap: [],
        };

        if (isGoodToHaveRecipeGroup(rg.code) && rg.recommendedProducts) {
            recipeSection.productsMap = rg.recommendedProducts.recipeProductsData
                .filter((rpd) => !!rpd.entryNumber)
                .map((rpd) => rpd.code);
        } else {
            recipeSection.productsMap = rg.recipeEntries.map((re) => re.orderEntry.product.code);
            recipeSection.sharedProductsMap = allSharedRecipeEntries
                .filter((re) =>
                    re.recipes?.some((sharedRecipe) => sharedRecipe.code === recipeSection.code),
                )
                .map((re) => re.orderEntry.product.code);
        }

        recipes.push(recipeSection);
    });

    return recipes;
};

export const mapCart = (hybrisCart: HybrisCart): ApiCart => {
    try {
        const cart: ApiCart = {
            items: mapCartItems(hybrisCart, hybrisCart.entries),
            cartData: mapCartData(hybrisCart),
            editedOrderItems: mapCartItems(hybrisCart, hybrisCart.editedEntries || []),
            promos: mapProductPromos(hybrisCart),
            recipes: mapRecipes(hybrisCart),
            ecoupons: mapEcoupons(hybrisCart.ecoupons),
        };

        return cart;
    } catch (error) {
        if (error instanceof Error)
            appInsights.trackException({
                exception: error,
            });
        throw error;
    }
};
